import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import axios from "axios";
import { ChevronsUpDown, Shield, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

interface UserOption {
  id: number;
  name: string | null;
  email: string;
}

/**
 * 管理者がユーザーをまたいで切り替えるための右上 Combobox。
 * - 管理者 (current_admin) がサインインしている時のみ Layout 側で描画される
 * - 入力は debounce (250ms) して /admins/users/search に投げる
 * - 選択で POST /admins/users/:id/login_as → 通常 user として再サインイン
 */
export function AdminUserSwitcher() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      axios
        .get('/admins/users/search', { params: { q: query } })
        .then((res) => {
          setUsers(res.data?.users || []);
        })
        .catch(() => {
          setUsers([]);
        })
        .finally(() => setLoading(false));
    }, 250);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [open, query]);

  const handleSelect = (user: UserOption) => {
    if (switching) return;
    setSwitching(true);
    router.post(
      `/admins/users/${user.id}/login_as`,
      {},
      {
        onFinish: () => {
          setSwitching(false);
          setOpen(false);
        },
      }
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="hidden md:inline-flex items-center gap-2 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
        >
          <Shield className="h-3.5 w-3.5" />
          <span>アカウント切り替え</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="名前 or メールで検索..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                検索中...
              </div>
            ) : (
              <>
                <CommandEmpty>該当するユーザーが見つかりません</CommandEmpty>
                <CommandGroup>
                  {users.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={`${user.id}-${user.email}`}
                      onSelect={() => handleSelect(user)}
                      disabled={switching}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {user.name || user.email}
                        </span>
                        {user.name && (
                          <span className="text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
